import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Apple,
  Beef,
  Carrot,
  ClipboardPlus,
  Cookie,
  Filter,
  Milk,
  Plus,
  Search,
  Trash2,
  Utensils,
  Wheat,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import {
  buildUnifiedFoodList,
  filterFoods,
  FOOD_FILTERS,
  formatFoodServing,
  getFoodCategories,
  getFoodIconKey,
  getLocalizedFoodName,
  getLocalizedFoodTypeLabel,
} from "../lib/v2FoodUtils";
import { deleteCustomFood, listCustomFoods } from "../lib/v2Database";
import { deleteV2CustomFood, loadV2CustomFoods, loadV2MainFoods } from "../lib/v2Storage";

const iconMap = {
  food: Utensils,
  custom: Cookie,
  fruit: Apple,
  vegetable: Carrot,
  protein: Beef,
  grain: Wheat,
  dairy: Milk,
  meal: Utensils,
  manual: ClipboardPlus,
};

export default function V2Foods() {
  const { lang } = useLang();
  const { isGuest, isRegistered, user } = useV2Session();
  const copy = getV2Copy(lang);
  const foodsCopy = copy.foodsCopy;
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(FOOD_FILTERS.all);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [customFoods, setCustomFoods] = useState(() => loadV2CustomFoods({ isGuest }));
  const [loadingCustomFoods, setLoadingCustomFoods] = useState(false);

  const mainFoods = useMemo(() => loadV2MainFoods(), []);
  const foods = useMemo(() => buildUnifiedFoodList(mainFoods, customFoods), [mainFoods, customFoods]);
  const categories = useMemo(() => getFoodCategories(foods), [foods]);
  const filteredFoods = useMemo(
    () => filterFoods(foods, { search, type: typeFilter, category: categoryFilter }),
    [foods, search, typeFilter, categoryFilter]
  );

  useEffect(() => {
    let alive = true;

    async function loadCustomFoods() {
      if (isRegistered && user?.id) {
        setLoadingCustomFoods(true);
        const foods = await listCustomFoods(user.id).catch(() => null);
        if (alive && foods) setCustomFoods(foods);
        if (alive) setLoadingCustomFoods(false);
        return;
      }

      setCustomFoods(loadV2CustomFoods({ isGuest }));
      setLoadingCustomFoods(false);
    }

    loadCustomFoods();
    return () => {
      alive = false;
    };
  }, [isGuest, isRegistered, user?.id]);

  const handleDelete = async (food) => {
    const foodName = getLocalizedFoodName(food, lang);
    const confirmed = window.confirm(formatCopy(foodsCopy.deleteConfirm, { name: foodName }));
    if (!confirmed) return;

    const result =
      isRegistered && user?.id
        ? await deleteCustomFood(user.id, food.id).catch(() => ({ ok: false, deletedId: null }))
        : deleteV2CustomFood(food.id, { isGuest });
    if (!result.ok) {
      toast.error(foodsCopy.deleteError);
      return;
    }

    if (isRegistered && user?.id) {
      setCustomFoods((current) => current.filter((item) => item.id !== food.id));
    } else {
      setCustomFoods(loadV2CustomFoods({ isGuest }));
    }
    toast.success(foodsCopy.deleteSuccess);
  };

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <header className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-primary">{copy.brand}</p>
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">{foodsCopy.title}</h1>
          <p className="max-w-3xl text-muted-foreground">{foodsCopy.description}</p>
        </div>
        <Button asChild>
          <Link to="/foods/add">
            <Plus className="h-4 w-4" />
            {foodsCopy.addCustomFood}
          </Link>
        </Button>
      </header>

      <section className="rounded-lg border border-border bg-card p-4">
        <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto] lg:items-center">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={foodsCopy.searchPlaceholder}
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <FilterButton active={typeFilter === FOOD_FILTERS.all} onClick={() => setTypeFilter(FOOD_FILTERS.all)}>
              {foodsCopy.all}
            </FilterButton>
            <FilterButton
              active={typeFilter === FOOD_FILTERS.verified}
              onClick={() => setTypeFilter(FOOD_FILTERS.verified)}
            >
              {foodsCopy.verified}
            </FilterButton>
            <FilterButton
              active={typeFilter === FOOD_FILTERS.custom}
              onClick={() => setTypeFilter(FOOD_FILTERS.custom)}
            >
              {foodsCopy.custom}
            </FilterButton>
          </div>

          <label className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              className="bg-transparent text-sm outline-none"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="all">{foodsCopy.allCategories}</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filteredFoods.map((food) => (
          <FoodCard key={`${food.type}-${food.id}`} food={food} lang={lang} copy={foodsCopy} onDelete={handleDelete} />
        ))}
      </div>

      {filteredFoods.length === 0 && (
        <section className="rounded-lg border border-border bg-card p-8 text-center">
          <h2 className="text-lg font-bold">{loadingCustomFoods ? foodsCopy.loading : foodsCopy.noFoodsTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{foodsCopy.noFoodsDescription}</p>
        </section>
      )}
    </section>
  );
}

function FoodCard({ food, lang, copy, onDelete }) {
  const Icon = iconMap[getFoodIconKey(food.icon_key)] || Utensils;
  const isCustom = food.type === "custom";
  const foodName = getLocalizedFoodName(food, lang);
  const typeLabel = getLocalizedFoodTypeLabel(food, copy);

  return (
    <article className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-extrabold">{foodName}</h2>
            <p className="text-sm text-muted-foreground">{food.category || copy.uncategorized}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${
            isCustom ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
          }`}
        >
          {typeLabel}
        </span>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-background p-3 text-sm">
        <span className="block text-xs font-medium text-muted-foreground">{copy.serving}</span>
        <span className="font-bold">
          {formatFoodServing(food)}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <Nutrient label={copy.potassiumMg.replace(" (mg)", "")} value={food.potassium_mg} unit="mg" />
        <Nutrient label={copy.phosphorusMg.replace(" (mg)", "")} value={food.phosphorus_mg} unit="mg" />
        <Nutrient label={copy.sodiumMg.replace(" (mg)", "")} value={food.sodium_mg} unit="mg" />
        <Nutrient label={copy.proteinG.replace(" (g)", "")} value={food.protein_g} unit="g" />
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground">{copy.source}: {food.source || copy.localFoodList}</p>
        <div className="flex gap-2">
          {isCustom && (
            <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => onDelete(food)}>
              <Trash2 className="h-4 w-4" />
              {copy.delete}
            </Button>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to={`/food-logging?food_id=${encodeURIComponent(food.id)}`}>
              <ClipboardPlus className="h-4 w-4" />
              {copy.logFood}
            </Link>
          </Button>
        </div>
      </div>
    </article>
  );
}

function formatCopy(template = "", values = {}) {
  return Object.entries(values).reduce((result, [key, value]) => result.replace(`{${key}}`, value), template);
}

function FilterButton({ active, children, onClick }) {
  return (
    <Button type="button" variant={active ? "default" : "outline"} onClick={onClick}>
      {children}
    </Button>
  );
}

function Nutrient({ label, value, unit }) {
  return (
    <div className="rounded-lg bg-muted px-3 py-2">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="font-bold">
        {formatAmount(value)} {unit}
      </span>
    </div>
  );
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
